import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class TabCoordinatorService implements OnDestroy {
    private readonly channelName = 'app-tab-coordination';
    private channel = new BroadcastChannel('tab-check');
    private sessionKey = 'unique_session_id';
    private takeoverKey = 'cipher_tab_takeover';
    private claimTimestampKey = 'cipher_primary_claim_at';
    private claimGuardMs = 2500;
    private isYielding = false;
    private focusTabNotification: Notification | null = null;

    private broadcastChannel: BroadcastChannel;
    private tabId = Math.random().toString(36).substr(2, 9);
    private activeCallTab: string | null = null;
    private lastHeartbeat: any = Date.now();

    private dataSubject = new Subject<any>();
    private leadershipSubject = new Subject<boolean>();
    private callStateSubject = new Subject<string>();
    public callState: any = 'idle'; // 'idle' | 'ringing' | 'ongoing'

    constructor(private router: Router, private ngZone: NgZone, private translate: TranslateService) {
        if (!window.opener && window.location.pathname !== '/block') {
            this.initTabCheck();
            // if (sessionStorage.getItem(this.sessionKey)) {
            //     this.tabId = sessionStorage.getItem(this.sessionKey);
            // } else {
            //     sessionStorage.setItem(this.sessionKey, this.tabId);
            // }

            this.callState = localStorage.getItem('activeCallStatus');
            this.activeCallTab = localStorage.getItem('activeCallTab');
            this.initializeBroadcastChannel();
            this.initializeStorageListeners();
            this.initializeVisibilityListener();
            // this.initializeHeartBeatListener();
        }
    }

    // Initialize HeartBeat Listner
    private initializeHeartBeatListener(): void {
        setInterval(() => {
            if (this.isCurrentTabLeader()) {
                this.broadcastChannel.postMessage({
                    type: 'HEARTBEAT',
                    tabId: this.tabId
                });
            } else if (Date.now() - this.lastHeartbeat > 3000) {
                console.info('Leader tab is unresponsive. Attempting to claim leadership...');
                this.claimLeadership();
            }
        }, 1000)
    }
    // Initialize communication channel
    private initializeBroadcastChannel(): void {
        this.broadcastChannel = new BroadcastChannel(this.channelName);

        this.broadcastChannel.onmessage = (event) => {
            switch (event.data.type) {
                case 'LEADERSHIP_CLAIM':
                    this.handleLeadershipClaim(event.data.tabId);
                    break;
                case 'DATA_UPDATE':
                    this.dataSubject.next(event.data.payload);
                    break;
                case 'CALL_ANSWERED':
                    this.handleCallAnswered(event.data.tabId);
                    break;
                case 'REQUEST_LEADER':
                    if (this.isCurrentTabLeader()) {
                        if (document.hidden && Notification.permission === 'granted') {
                            new Notification('Switch to leader tab', {
                                body: 'Switch back to the leader tab!',
                                icon: 'assets/images/logo-small.png', // Optional
                            });
                        }
                    }
                    break;
                case 'HEARTBEAT':
                    this.lastHeartbeat = Date.now();
                    break;
                case 'CALL_STATE_UPDATE':
                    this.handleCallStateUpdate(event.data.state);
                    break;
            }
        };
    }




    // Handle storage events for tab coordination
    private initializeStorageListeners(): void {
        window.addEventListener('storage', (event) => {
            if (event.key === 'activeCallTab') {
                this.activeCallTab = event.newValue;
                this.leadershipSubject.next(this.isCurrentTabLeader());
            }
        });
    }

    // Handle tab visibility changes
    private initializeVisibilityListener(): void {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.checkLeadership();
            }
        });
    }

    // Claim leadership for handling calls
    public claimLeadership(callstate: any = 'idle'): void {
        localStorage.setItem('activeCallTab', this.tabId);
        this.broadcastChannel.postMessage({
            type: 'LEADERSHIP_CLAIM',
            tabId: this.tabId,
            timestamp: Date.now()
        });
        this.setCallState(callstate);
        this.activeCallTab = this.tabId;
        this.leadershipSubject.next(true);
        this.broadcastChannel.postMessage({
            type: 'HEARTBEAT',
            tabId: this.tabId
        });
    }

    // Check if current tab is the leader
    public isCurrentTabLeader(): boolean {
        return this.activeCallTab === this.tabId;
    }

    // Handle incoming leadership claims
    private handleLeadershipClaim(tabId: string): void {
        if (tabId !== this.tabId) {
            this.activeCallTab = tabId;
            localStorage.setItem('activeCallTab', tabId);
            this.leadershipSubject.next(false);
        }
    }

    // Handle call answering across tabs
    private handleCallAnswered(tabId: string): void {
        this.activeCallTab = tabId;
        localStorage.setItem('activeCallTab', tabId);
        this.leadershipSubject.next(this.isCurrentTabLeader());
    }

    // Notify other tabs when a call is answered
    public notifyCallAnswered(): void {
        // this.claimLeadership();
        this.broadcastChannel.postMessage({
            type: 'CALL_ANSWERED',
            tabId: this.tabId
        });
    }

    // Sync data between tabs
    public sendDataUpdate(payload: any): void {
        this.broadcastChannel.postMessage({
            type: 'DATA_UPDATE',
            payload: payload,
            tabId: this.tabId
        });
    }

    // Get observable for data updates
    public getDataUpdates(): Observable<any> {
        return this.dataSubject.asObservable().pipe(
            filter(data => data !== undefined)
        );
    }

    // Get observable for leadership changes
    public getLeadershipUpdates(): Observable<boolean> {
        return this.leadershipSubject.asObservable();
    }

    // Cleanup
    ngOnDestroy(): void {
        this.broadcastChannel.close();
        this.channel.close();
        if (this.isCurrentTabLeader()) {
            localStorage.removeItem('activeCallTab');
        }
    }

    // Check leadership status periodically
    private checkLeadership(): void {
        const storedLeader = localStorage.getItem('activeCallTab');
        if (storedLeader !== this.activeCallTab) {
            this.activeCallTab = storedLeader;
            this.leadershipSubject.next(this.isCurrentTabLeader());
        }
    }

    public redirectToLeaderTab() {
        this.broadcastChannel.postMessage({
            type: 'REQUEST_LEADER',
            timestamp: Date.now()
        });
    }

    public setCallState(state: 'idle' | 'ringing' | 'ongoing'): void {
        // console.log('setCallState', state);
        this.callState = state;
        localStorage.setItem('activeCallStatus', state);
        this.callStateSubject.next(state);
        this.broadcastCallState();
    }

    private broadcastCallState(): void {
        this.broadcastChannel.postMessage({
            type: 'CALL_STATE_UPDATE',
            state: this.callState,
            tabId: this.tabId
        });
    }
    private handleCallStateUpdate(state: 'idle' | 'ringing' | 'ongoing'): void {
        // console.log('handleCallStateUpdate', state)
        localStorage.setItem('activeCallStatus', state);
        this.callState = state;
        // Notify components or services about the call state change
        this.callStateSubject.next(state);
    }

    public canAnswerCall(): boolean {
        return this.isCurrentTabLeader() && this.callState === 'ringing';
    }

    public canInitiateCall(): boolean {
        return this.isCurrentTabLeader() && this.callState === 'idle';
    }
    public canInitiateCallByState(): boolean {
        // console.log(localStorage.getItem('activeCallStatus'), this.callState)
        return this.callState === 'idle';
    }

    private initTabCheck() {
        this.tabId = this.generateUniqueId();

        // If no primary tab exists, set this tab as primary
        if (!sessionStorage.getItem(this.sessionKey)) {
            sessionStorage.setItem(this.sessionKey, this.tabId);
        }

        this.channel.onmessage = (event) => this.handleTabCheckMessage(event);
        // Ask other tabs if they exist
        this.channel.postMessage({ type: 'check-tab', route: window.location.pathname });

        if (sessionStorage.getItem(this.takeoverKey) === '1') {
            setTimeout(() => sessionStorage.removeItem(this.takeoverKey), 1500);
        }
    }

    private handleTabCheckMessage(event: MessageEvent): void {
        const data = event.data || {};
        switch (data.type) {
            case 'check-tab':
                if (this.isYielding) {
                    return;
                }
                this.channel.postMessage({ type: 'tab-exists', primaryTabId: sessionStorage.getItem(this.sessionKey) });
                if (data.route) {
                    this.ngZone.run(() => {
                        this.router.navigate([data.route]);
                    });
                }
                break;
            case 'ping-primary':
                if (!this.isYielding) {
                    this.channel.postMessage({ type: 'primary-alive', primaryTabId: sessionStorage.getItem(this.sessionKey) });
                }
                break;
            case 'tab-exists':
            case 'primary-alive':
                if (data.primaryTabId !== this.tabId) {
                    if (sessionStorage.getItem(this.takeoverKey) === '1') {
                        this.channel.postMessage({ type: 'claim-primary', tabId: this.tabId });
                        return;
                    }
                    console.warn('Duplicate tab detected! Closing...');
                    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
                    window.location.href = `/block?returnUrl=${returnUrl}`;
                }
                break;
            case 'claim-primary':
                if (data.tabId !== this.tabId) {
                    this.yieldPrimaryTab();
                }
                break;
            case 'request-focus':
                if (!this.isYielding) {
                    this.tryFocusThisTab();
                }
                break;
        }
    }

    private yieldPrimaryTab(): void {
        this.isYielding = true;
        this.channel.onmessage = null;
        window.location.href = '/block';
    }

    public takeOverAsPrimary(returnUrl: string = '/'): void {
        if (!this.tabId) {
            this.tabId = this.generateUniqueId();
        }
        sessionStorage.setItem(this.takeoverKey, '1');
        sessionStorage.setItem(this.sessionKey, this.tabId);
        localStorage.setItem(this.claimTimestampKey, Date.now().toString());
        this.channel.postMessage({ type: 'claim-primary', tabId: this.tabId });
        window.location.href = this.sanitizeReturnUrl(returnUrl);
    }

    public wasRecentlyClaimed(guardMs: number = this.claimGuardMs): boolean {
        const raw = localStorage.getItem(this.claimTimestampKey);
        if (!raw) {
            return false;
        }
        const timestamp = Number(raw);
        return Number.isFinite(timestamp) && Date.now() - timestamp < guardMs;
    }

    public requestExistingTabFocus(): void {
        this.channel.postMessage({ type: 'request-focus', timestamp: Date.now() });
        if (typeof Notification === 'undefined') {
            return;
        }
        if (Notification.permission === 'granted') {
            this.showFocusNotificationWithUserGesture();
            return;
        }
        if (Notification.permission === 'default') {
            Notification.requestPermission().then((permission) => {
                if (permission === 'granted') {
                    this.showFocusNotificationWithUserGesture();
                }
            });
        }
    }

    private showFocusNotificationWithUserGesture(): void {
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
            return;
        }
        const title = document.title || this.translate.instant('Cipher');
        const options: NotificationOptions = {
            body: this.translate.instant('Click to switch to the open Cipher tab.'),
            icon: 'assets/images/logo-small.png',
            tag: 'cipher-focus-tab',
            data: { action: 'focus-tab' }
        };
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then((registration) => {
                return registration.showNotification(title, options);
            }).catch(() => {
                this.showFocusTabNotification(title);
            });
            return;
        }
        this.showFocusTabNotification(title);
    }

    public probePrimaryTab(timeoutMs: number = 1000): Promise<boolean> {
        return new Promise((resolve) => {
            let settled = false;
            const finish = (alive: boolean) => {
                if (settled) {
                    return;
                }
                settled = true;
                this.channel.removeEventListener('message', handleMessage);
                this.ngZone.run(() => resolve(alive));
            };
            const handleMessage = (event: MessageEvent) => {
                if (event.data?.type === 'primary-alive' || event.data?.type === 'tab-exists') {
                    finish(true);
                }
            };
            this.channel.addEventListener('message', handleMessage);
            this.channel.postMessage({ type: 'ping-primary', timestamp: Date.now() });
            setTimeout(() => finish(false), timeoutMs);
        });
    }

    private tryFocusThisTab(): void {
        try {
            window.focus();
        } catch { }
        const originalTitle = document.title;
        let tick = 0;
        const intervalId = window.setInterval(() => {
            document.title = tick % 2 === 0 ? `▶ ${originalTitle}` : originalTitle;
            tick++;
            if (tick > 12) {
                window.clearInterval(intervalId);
                document.title = originalTitle;
            }
        }, 400);
        this.showFocusTabNotification(originalTitle);
    }

    private showFocusTabNotification(originalTitle: string): void {
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
            return;
        }
        if (this.focusTabNotification) {
            this.focusTabNotification.close();
            this.focusTabNotification = null;
        }
        const notification = new Notification(originalTitle || this.translate.instant('Cipher'), {
            body: this.translate.instant('Click to switch to the open Cipher tab.'),
            icon: 'assets/images/logo-small.png',
            tag: 'cipher-focus-tab'
        });
        notification.onclick = (event) => {
            event.preventDefault();
            window.focus();
            notification.close();
        };
        this.focusTabNotification = notification;
    }

    private sanitizeReturnUrl(url: string): string {
        if (!url || !url.startsWith('/') || url.startsWith('//') || url.startsWith('/block')) {
            return '/';
        }
        return url;
    }

    private generateUniqueId(): string {
        return Math.random().toString(36).substring(2) + Date.now().toString(36);
    }
}